import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { DXC_SCENE_FOR, DxcSceneName, DxcSceneTone } from '../dxc.types';
import { resolveDxcErrorHint } from './dxc-error-hints';

/**
 * Diálogos bloqueantes del sistema.
 *
 * Sustituye a SweetAlert, y no como un cambio de piel: las 269 llamadas a
 * `Swal.fire` del producto hacían **cuatro trabajos distintos con la misma
 * caja**, y ese es el defecto real. La regla del sistema ya los separa (§6):
 *
 *   · alerta        → persistente y dentro del flujo  → `.dxc-alert`
 *   · notificación  → efímera y sobre lo ya ocurrido  → `ToastService`
 *   · diálogo       → bloqueante                      → esto
 *
 * De ahí que 73 de esas llamadas —los avisos de éxito— no vengan aquí: parar al
 * usuario con un modal para decirle que algo salió bien le cobra una pulsación
 * por una noticia que no cambia lo que iba a hacer. Van al `ToastService`. Aquí
 * llega solo lo que de verdad detiene: una decisión que pedir, un desenlace que
 * cierra un flujo y un fallo que impide seguir.
 */

export type DxcConfirmRequest = {
    /** Nombra el objeto concreto: «Dar de baja la unidad ABC456», nunca «¿Estás seguro?». */
    title: string;
    /** Qué pasa y, en las destructivas, qué NO se pierde. */
    description?: string;
    /** Repite el verbo del título. Nunca «Aceptar». */
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'default' | 'danger';
    /**
     * Escena del lienzo. Se omite y la deduce el tipo; solo se nombra cuando el
     * dominio tiene algo más preciso que decir —`sello-rechazado` en vez del
     * fallo genérico—.
     */
    scene?: DxcSceneName;
};

export type DxcOutcomeKind = 'success' | 'error' | 'warning' | 'info';

export type DxcOutcomeRequest = {
    title: string;
    description?: string;
    /**
     * El código que devuelve el sistema: `CFDI40102`, el folio, el id de la
     * operación. Un error dice qué pasó **y** qué hacer (§6): la descripción es
     * la traducción al paso siguiente, y esto es lo que se pega en un ticket.
     */
    code?: string;
    /** Volcado largo del backend. Va plegado — desplegado empuja el pie fuera de la pantalla. */
    detail?: string;
    /** Qué hacer ahora. Repite el verbo; «Aceptar» no dice nada. */
    closeLabel?: string;
    scene?: DxcSceneName;
    tone?: DxcSceneTone;
    /**
     * Volcado técnico + «Copiar para soporte». Lo pone `failure()` cuando el
     * fallo NO es del usuario: es la diferencia entre un error accionable y uno
     * que solo se puede reportar.
     */
    support?: boolean;
};

export type DxcPromptRequest = {
    title: string;
    description?: string;
    label: string;
    placeholder?: string;
    value?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Devuelve el mensaje de error, o vacío si el valor sirve. */
    validate?: (value: string) => string;
};

/**
 * Una decisión con dos salidas además de cancelar. Encadenar dos `confirm()` para
 * una sola pregunta obliga a adivinar qué significaba el «no» de la primera.
 */
export type DxcChoiceRequest = {
    title: string;
    description?: string;
    /** En orden de lectura. Una sola `primary` —la recomendada—, que va la última, junto al canto. */
    options: { value: string; label: string; primary?: boolean }[];
    cancelLabel?: string;
    scene?: DxcSceneName;
};

type PendingConfirm = DxcConfirmRequest & { mode: 'confirm'; resolve: (ok: boolean) => void };
type PendingOutcome = DxcOutcomeRequest & { mode: 'outcome'; kind: DxcOutcomeKind; resolve: (ok: boolean) => void };
type PendingPrompt  = DxcPromptRequest  & { mode: 'prompt';  resolve: (value: string | null) => void };
type PendingChoice  = DxcChoiceRequest  & { mode: 'choice';  resolve: (value: string | null) => void };

export type DxcPending = PendingConfirm | PendingOutcome | PendingPrompt | PendingChoice;

/** Tono del lienzo por tipo de desenlace. Mismo eje semántico que la pastilla (§5). */
const TONE_FOR: Record<DxcOutcomeKind, DxcSceneTone> = {
    success: 'success', error: 'danger', warning: 'warning', info: 'info',
};

@Injectable({ providedIn: 'root' })
export class DxcDialogService {

    readonly $pending = new BehaviorSubject<DxcPending | null>(null);

    /**
     * Verdadero mientras el diálogo se está yendo. El anfitrión lo pinta como
     * `is-closing` y lo mantiene montado hasta que la animación termina.
     *
     * Vive en el servicio y no en el componente porque **la promesa no puede
     * resolverse antes que la salida**: si `answer(true)` resolviera al
     * instante, la vista navegaría o abriría el siguiente diálogo con este
     * todavía en pantalla, y se verían los dos.
     */
    readonly $closing = new BehaviorSubject(false);

    // ── Decisión ───────────────────────────────────────────────────────────

    confirm(request: DxcConfirmRequest): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            this.$pending.next({
                ...request,
                mode: 'confirm',
                confirmLabel: request.confirmLabel ?? 'Aceptar',
                cancelLabel: request.cancelLabel ?? 'Cancelar',
                variant: request.variant ?? 'default',
                resolve,
            });
        });
    }

    /** Atajo para destructivas: pinta la confirmación en rojo. */
    confirmDanger(request: DxcConfirmRequest) {
        return this.confirm({ ...request, variant: 'danger' });
    }

    // ── Desenlace ──────────────────────────────────────────────────────────

    outcome(kind: DxcOutcomeKind, request: DxcOutcomeRequest): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            this.$pending.next({
                ...request,
                mode: 'outcome',
                kind,
                // Van DESPUÉS del spread a propósito: `{ ...request }` con una
                // clave presente y en `undefined` —que es lo que devuelve
                // `options.scene` la mayoría de las veces— pisa el valor por
                // defecto con nada. El `??` es lo único que distingue «no lo
                // dije» de «lo dije vacío».
                closeLabel: request.closeLabel ?? 'Entendido',
                scene: request.scene ?? DXC_SCENE_FOR[kind],
                tone: request.tone ?? TONE_FOR[kind],
                resolve,
            });
        });
    }

    /**
     * Un éxito que **cierra un flujo**: la carta porte timbrada, la carga
     * entregada, el flete asignado. Solo eso.
     *
     * Si el éxito no cierra nada —un borrador guardado, un filtro aplicado— no
     * es un diálogo: es `ToastService.success()`. La diferencia no es de énfasis
     * sino de si el usuario tiene algo que decidir después.
     */
    success(request: DxcOutcomeRequest) { return this.outcome('success', request); }

    warning(request: DxcOutcomeRequest) { return this.outcome('warning', request); }

    info(request: DxcOutcomeRequest) { return this.outcome('info', request); }

    /**
     * El fallo de una operación, a partir de lo que sea que devolvió el backend.
     *
     * Dos decisiones, y las dos salen de mirar lo que el producto enseña hoy.
     *
     * **El título se escribe solo, a partir del verbo.** Las 126 llamadas de
     * error eran casi todas `Swal.fire('Error', error.message, 'error')`, y
     * entre ellas convivían «Error», «error», «Error!» y «Ups». Un desenlace se
     * nombra por lo que NO se pudo hacer, no por su categoría.
     *
     * **El mensaje del backend NO es lo que el usuario lee.** Baja al detalle
     * plegado, y arriba va la traducción de `DXC_ERROR_HINTS`. Lo que hoy se
     * enseña en su lugar son frases como «El valor del campo SubTotal debe ser
     * cero '0'» — el esquema del SAT hablando, sobre un campo que el usuario no
     * escribe y no puede corregir. Cuando el código no está en la tabla, el
     * texto de respaldo tampoco es técnico: dice qué hacer y ofrece el detalle.
     *
     * ```ts
     * catch (error) { this.dialog.failure(error, { doing: 'timbrar la carta porte' }); }
     * ```
     */
    failure(error: unknown, options: { doing: string; scene?: DxcSceneName; description?: string } = { doing: 'completar la operación' }) {
        const raw = error as any;
        const rawMessage: string = raw?.error?.message ?? raw?.error?.error ?? raw?.message
            ?? (typeof raw === 'string' ? raw : '');
        // El código puede venir dentro del propio texto: el interceptor del PAC
        // devuelve «CRP20103: El valor del campo SubTotal…».
        const embedded = rawMessage.match(/\b(CRP\d{5}|CFDI\d{5})\b/)?.[1];

        // El orden importa y se decidió midiendo: el mismo fallo llegaba con
        // `status: 400` y con `CRP20103` dentro del texto, y enseñar el 400 es
        // enseñar el dato inútil — «400» no se puede buscar en ningún sitio y
        // «CRP20103» sí. El estado HTTP solo manda cuando no hay nada mejor.
        const resolved: string | undefined =
            raw?.error?.code ?? raw?.code ?? embedded
            ?? (raw?.status !== undefined ? String(raw.status) : undefined);

        const hint = resolveDxcErrorHint(resolved)
            ?? resolveDxcErrorHint(embedded)
            ?? resolveDxcErrorHint(raw?.status !== undefined ? String(raw.status) : undefined);

        const support = hint?.fix === 'soporte' || !hint;

        // ── Qué lee el usuario ──────────────────────────────────────────────
        //
        // La primera versión tenía un fallo de bulto y se vio en cuanto salió a
        // pantalla: cuando NO había traducción curada ponía de descripción «el
        // sistema no dijo por qué» y bajaba el mensaje del backend al detalle
        // plegado. Pero el backend sí lo había dicho —«La integración con el
        // servicio de financiamiento no está habilitada»—, así que el diálogo
        // **afirmaba lo contrario de lo que sabía** y escondía la única
        // explicación detrás de un pliegue que nadie abre.
        //
        // La regla correcta es una: **el usuario ve siempre lo más concreto que
        // tengamos**, y nada que explique el fallo vive plegado.
        //
        //   1. lo que diga la vista, que sabe más que nadie;
        //   2. la traducción curada, si existe — y entonces el texto crudo baja
        //      al detalle, porque ahí sí lo estamos sustituyendo;
        //   3. el mensaje del backend tal cual: no será perfecto, pero es
        //      información de verdad y **siempre gana a una frase genérica**;
        //   4. y solo si no hay nada, la frase genérica.
        //
        // Al detalle baja únicamente lo que NO se está enseñando ya: no se
        // pliega lo que el usuario tiene delante.
        const traducido = !options.description && !!hint;

        const description = options.description
            ?? hint?.message
            ?? (rawMessage || undefined)
            ?? 'No pudimos completar la operación y el sistema no devolvió un motivo. Vuelve a intentarlo; si sigue igual, comparte el código con soporte.';

        return this.outcome('error', {
            title: `No se pudo ${options.doing}`,
            description,
            code: resolved,
            detail: traducido && rawMessage ? rawMessage : undefined,
            support,
            scene: options.scene ?? hint?.scene,
            closeLabel: hint?.action ?? (support ? 'Entendido' : 'Volver a intentar'),
        });
    }

    // ── Un dato que pedir ──────────────────────────────────────────────────

    prompt(request: DxcPromptRequest): Promise<string | null> {
        return new Promise<string | null>(resolve => {
            this.$pending.next({
                ...request,
                mode: 'prompt',
                confirmLabel: request.confirmLabel ?? 'Guardar',
                cancelLabel: request.cancelLabel ?? 'Cancelar',
                value: request.value ?? '',
                resolve,
            });
        });
    }

    // ── Elegir entre varias salidas ────────────────────────────────────────

    /** Resuelve con el `value` de la opción elegida, o `null` si se cancela: botón, Esc o velo. */
    choose(request: DxcChoiceRequest): Promise<string | null> {
        return new Promise<string | null>(resolve => {
            this.$pending.next({
                ...request,
                mode: 'choice',
                cancelLabel: request.cancelLabel ?? 'Cancelar',
                resolve,
            });
        });
    }

    // ── Respuesta ──────────────────────────────────────────────────────────

    answer(ok: boolean) {
        const pending = this.$pending.value;
        if (!pending || pending.mode === 'prompt' || pending.mode === 'choice') return;
        this.#leave(() => pending.resolve(ok));
    }

    answerPrompt(value: string | null) {
        const pending = this.$pending.value;
        if (!pending || pending.mode !== 'prompt') return;
        this.#leave(() => pending.resolve(value));
    }

    answerChoice(value: string | null) {
        const pending = this.$pending.value;
        if (!pending || pending.mode !== 'choice') return;
        this.#leave(() => pending.resolve(value));
    }

    /**
     * Toca la salida, espera a que termine y entonces resuelve.
     *
     * La duración **se lee del token**, no se escribe aquí. Es la trampa que
     * la regla ya documenta con `bounceOutRight`: allí la duración de salida
     * vivía en un `setTimeout(300)` del componente además de en el CSS, así
     * que eran dos sitios que había que acordarse de cambiar juntos. Con
     * `--dxc-dur-modal` hay uno.
     *
     * Con movimiento reducido no hay animación que esperar, así que se cierra
     * en el acto: hacer esperar 200 ms a quien pidió que no haya movimiento es
     * darle lo peor de las dos cosas.
     */
    #leave(resolve: () => void) {
        const reduced = typeof matchMedia === 'function'
            && matchMedia('(prefers-reduced-motion: reduce)').matches;

        const done = () => {
            this.$closing.next(false);
            this.$pending.next(null);
            resolve();
        };

        if (reduced) { done(); return; }

        this.$closing.next(true);
        setTimeout(done, this.#exitMs());
    }

    /** El `× .55` de la salida vive en el CSS; aquí solo se lee el token. */
    #exitMs() {
        const raw = typeof getComputedStyle === 'function'
            ? getComputedStyle(document.documentElement).getPropertyValue('--dxc-dur-modal')
            : '';
        const ms = parseFloat(raw);
        return (Number.isFinite(ms) && ms > 0 ? ms : 360) * 0.55;
    }

    /** Cierre por Esc o por el velo: nunca confirma, nunca guarda. */
    dismiss() {
        const pending = this.$pending.value;
        if (!pending) return;
        if (pending.mode === 'prompt') this.answerPrompt(null);
        else if (pending.mode === 'choice') this.answerChoice(null);
        else this.answer(false);
    }
}
